import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import { showToast, startLoader, stopLoader } from "../../../lib/global";
import { breadcrumbAction } from "../../../redux/actions/config.action";
import { getAllCollections } from "../../../redux/actions/collections.action";
import {
  createDiscount,
  getAllDiscounts,
  getDiscountById,
  updateDiscountById,
} from "../../../redux/actions/discount.action";
import { estimateProductsAffected, findConflicts } from "../../../utils/discountConflicts";

function AddDiscount() {
  const { discountId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const [name, setName] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [collections, setCollections] = useState([]);
  const [allDiscounts, setAllDiscounts] = useState([]);
  const [serverConflicts, setServerConflicts] = useState([]);

  useEffect(() => {
    dispatch(
      breadcrumbAction([
        { label: "Products", url: "/products/all-products" },
        { label: "Discounts", url: "/products/discounts" },
        { label: discountId ? "Edit Discount" : "Add Discount", url: null },
      ])
    );

    startLoader();
    Promise.all([
      dispatch(getAllCollections({ limit: 9999, offset: 0 })),
      dispatch(getAllDiscounts()),
    ])
      .then(([collectionsRes, discountsRes]) => {
        if (collectionsRes?.data?.status === 200) {
          setCollections(collectionsRes.data.data || []);
        }
        if (discountsRes?.data?.data) {
          setAllDiscounts(discountsRes.data.data);
        }
        if (discountId) {
          return dispatch(getDiscountById(discountId));
        }
        return null;
      })
      .then((discountRes) => {
        if (discountRes?.data) {
          const d = discountRes.data;
          setName(d.name || "");
          setDiscountPct(String(d.discount_pct ?? ""));
          setStartDate(d.start_date ? moment(d.start_date).format("YYYY-MM-DD") : "");
          setEndDate(d.end_date ? moment(d.end_date).format("YYYY-MM-DD") : "");
          setSelectedCollectionIds(d.collection_ids || []);
        }
        stopLoader();
      })
      .catch(() => {
        stopLoader();
        showToast("Failed to load discount data", "error");
      });
  }, [discountId, dispatch]);

  const collectionNameById = useMemo(() => {
    const map = {};
    collections.forEach((c) => {
      map[c.collection_id] = c.name;
    });
    return map;
  }, [collections]);

  const selectedCollections = useMemo(
    () => collections.filter((c) => selectedCollectionIds.includes(c.collection_id)),
    [collections, selectedCollectionIds]
  );

  const clientConflicts = useMemo(
    () =>
      findConflicts({
        discounts: allDiscounts,
        collectionIds: selectedCollectionIds,
        startDate,
        endDate,
        excludeDiscountId: discountId || null,
        collectionNameById,
      }),
    [allDiscounts, selectedCollectionIds, startDate, endDate, discountId, collectionNameById]
  );

  const conflictCollectionIds = useMemo(() => {
    const ids = new Set();
    [...clientConflicts, ...serverConflicts].forEach((c) => ids.add(c.collection_id));
    return ids;
  }, [clientConflicts, serverConflicts]);

  const toggleCollection = (collectionId) => {
    setServerConflicts([]);
    setSelectedCollectionIds((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const validateForm = () => {
    if (!name.trim()) {
      showToast("Discount name is required", "error");
      return false;
    }
    const pct = Number(discountPct);
    if (!Number.isInteger(pct) || pct < 1 || pct > 99) {
      showToast("Discount percentage must be a whole number between 1 and 99", "error");
      return false;
    }
    if (!startDate || !endDate) {
      showToast("Start and end dates are required", "error");
      return false;
    }
    if (moment(endDate).isBefore(moment(startDate), "day")) {
      showToast("End date must be on or after start date", "error");
      return false;
    }
    if (!selectedCollectionIds.length) {
      showToast("Select at least one collection", "error");
      return false;
    }
    if (clientConflicts.length) {
      showToast("Resolve collection date conflicts before saving", "error");
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      name: name.trim(),
      discount_pct: Number(discountPct),
      start_date: startDate,
      end_date: endDate,
      collection_ids: selectedCollectionIds,
    };

    startLoader();
    setServerConflicts([]);
    try {
      const res = discountId
        ? await dispatch(updateDiscountById(discountId, payload))
        : await dispatch(createDiscount(payload));

      stopLoader();
      const status = res?.status;
      if (status === 201 || status === 200) {
        showToast(`Discount ${discountId ? "updated" : "created"} successfully`, "success");
        history.push("/products/discounts");
        return;
      }

      const body = res?.data;
      if (body?.conflicts?.length) {
        setServerConflicts(body.conflicts);
        showToast(body.message || "Promotion period conflict", "error");
        return;
      }
      showToast(body?.message || "Failed to save discount", "error");
    } catch (err) {
      stopLoader();
      const body = err?.response?.data;
      if (body?.conflicts?.length) {
        setServerConflicts(body.conflicts);
        showToast(body.message || "Promotion period conflict", "error");
        return;
      }
      showToast(body?.message || "Failed to save discount", "error");
    }
  };

  const previewCollections =
    selectedCollections.map((c) => c.name).join(", ") || "No collections selected";
  const previewProducts = estimateProductsAffected(selectedCollections);
  const activeConflicts = serverConflicts.length ? serverConflicts : clientConflicts;

  return (
    <div className="card mx-3 p-4 shadow-sm border-0">
      <h5 className="font-weight-bold mb-4">{discountId ? "Edit Discount" : "Add Discount"}</h5>

      <div className="row">
        <div className="col-lg-8">
          <div className="form-group">
            <label>Discount Name *</label>
            <input
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Summer Fiction Sale"
            />
          </div>

          <div className="row">
            <div className="col-md-4 form-group">
              <label>Discount % *</label>
              <input
                type="number"
                min={1}
                max={99}
                step={1}
                className="form-control"
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
                placeholder="1–99"
              />
            </div>
            <div className="col-md-4 form-group">
              <label>Start date *</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setServerConflicts([]);
                  setStartDate(e.target.value);
                }}
              />
            </div>
            <div className="col-md-4 form-group">
              <label>End date *</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setServerConflicts([]);
                  setEndDate(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="font-weight-bold">Collections *</label>
            <p className="small text-muted mb-2">
              Select one or more collections to apply this discount.
            </p>
            {collections.length === 0 ? (
              <div className="alert alert-warning">
                No collections found. Create collections under Products first.
              </div>
            ) : (
              <div className="border rounded-lg p-3" style={{ maxHeight: 320, overflowY: "auto" }}>
                {collections.map((collection) => {
                  const checked = selectedCollectionIds.includes(collection.collection_id);
                  const hasConflict = conflictCollectionIds.has(collection.collection_id);
                  return (
                    <label
                      key={collection.collection_id}
                      className={`d-flex align-items-center mb-2 p-2 rounded ${
                        hasConflict ? "bg-light-danger border border-danger" : ""
                      }`}
                      style={hasConflict ? { backgroundColor: "#fff5f5" } : undefined}
                    >
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={checked}
                        onChange={() => toggleCollection(collection.collection_id)}
                      />
                      <span className="flex-grow-1">
                        <strong>{collection.name}</strong>
                        <span className="text-muted small ml-2">
                          ({collection.product_count || 0} products)
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {activeConflicts.length > 0 && (
            <div className="alert alert-danger mt-3">
              <strong>Promotion period conflict</strong>
              <ul className="mb-0 mt-2 pl-3">
                {activeConflicts.map((c) => (
                  <li key={`${c.collection_id}-${c.discount_id}`}>
                    <strong>{c.collection_name}</strong> conflicts with{" "}
                    <strong>{c.discount_name}</strong> (
                    {moment(c.start_date).format("DD MMM YYYY")} –{" "}
                    {moment(c.end_date).format("DD MMM YYYY")})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="col-lg-4">
          <div className="card bg-light border-0 p-3">
            <h6 className="font-weight-bold">Live preview</h6>
            {discountPct && startDate && endDate ? (
              <>
                <p className="mb-2">
                  <span className="badge badge-danger mr-1">{discountPct}% OFF</span>
                  on selected collections
                </p>
                <p className="small mb-1">
                  <strong>Collections:</strong> {previewCollections}
                </p>
                <p className="small mb-1">
                  <strong>Period:</strong> {moment(startDate).format("DD MMM YYYY")} –{" "}
                  {moment(endDate).format("DD MMM YYYY")}
                </p>
                <p className="small mb-0">
                  <strong>Est. products affected:</strong> {previewProducts}
                  <span className="text-muted"> (approximate)</span>
                </p>
              </>
            ) : (
              <p className="text-muted small mb-0">Fill in percentage and dates to see preview.</p>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-light mr-2"
          onClick={() => history.push("/products/discounts")}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-dark" onClick={onSubmit}>
          Save Discount
        </button>
      </div>
    </div>
  );
}

export default AddDiscount;
